// CSS top-level rule splitter for diff-patch calculations.
// Example: "@import ...; .a{...}" -> ["@import ...;", ".a{...}"].

// Split CSS text into top-level rule strings using a brace-aware scanner.
export function splitTopLevelCssRules(css: string): string[] {
  const rules: string[] = []
  let current = ''
  let depth = 0
  let quote: "'" | '"' | null = null
  let inBlockComment = false

  // Scan CSS and flush each completed top-level rule.
  for (let index = 0; index < css.length; index += 1) {
    const char = css[index]
    const next = css[index + 1] ?? ''
    const previous = index > 0 ? css[index - 1] : ''

    // Keep comment content opaque until `*/` closes.
    if (inBlockComment) {
      current += char
      if (char === '*' && next === '/') {
        current += '/'
        index += 1
        inBlockComment = false
      } else {
        // Continue scanning block comment content.
      }
      continue
    } else {
      // Continue scanner outside block comments.
    }

    // Keep quoted strings opaque to brace and statement scanning.
    if (quote) {
      current += char
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue scanning quoted css text.
      }
      continue
    } else {
      // Continue scanner outside quoted segments.
    }

    // Enter comment mode when scanner sees `/*`.
    if (char === '/' && next === '*') {
      current += '/*'
      index += 1
      inBlockComment = true
      continue
    } else if (char === "'" || char === '"') {
      // Enter quote mode for `'...'` and `"..."` segments.
      quote = char
      current += char
      continue
    } else {
      // Continue structural rule parsing.
    }

    if (char === '{') {
      depth += 1
      current += char
      continue
    } else if (char === '}') {
      if (depth > 0) depth -= 1
      current += char
      // Flush full block rule when scanner returns to top level.
      if (depth === 0) {
        pushTrimmedRule(rules, current)
        current = ''
      } else {
        // Keep collecting nested block characters.
      }
      continue
    } else {
      // Continue non-brace token handling.
    }

    // Flush top-level statement rules like `@import ...;`.
    if (char === ';' && depth === 0) {
      current += char
      pushTrimmedRule(rules, current)
      current = ''
      continue
    } else {
      // Keep collecting current rule text.
    }

    current += char
  }

  // Push trailing text when scanner ended on a non-empty rule buffer.
  pushTrimmedRule(rules, current)
  // Return top-level css rule list.
  return rules
}

// Push trimmed rule only when buffer contains non-whitespace content.
function pushTrimmedRule(rules: string[], rawRule: string): void {
  const trimmed = rawRule.trim()
  // Skip empty buffers produced by inter-rule whitespace.
  if (!trimmed) {
    return
  } else {
    // Push non-empty trimmed rule text.
    rules.push(trimmed)
  }
}
